import {
  BaseApiRequestParams,
  BaseListResponse,
  ContentOrderBy,
  GerberaRequestFactory,
  ImageContent,
  mapToRequestParams,
  PublishStatus
} from './core'
import { DataUri } from '../../misc/data-uri'
import { StorageData } from '../../store/manager/state'

/**
 * GET コンテンツ1件を取得する際に必要な情報
 */
export interface GetContentRequest {
  siteId: number
  contentId: number
}

export interface ContentDataSet {
  content: ContentItem
  images: ImageContent[]
  ewml: string
  css: string
}

// GET コンテンツ一覧を取得するリクエスト際の時に必要な情報
// （GetContentListRequestParamsと型の指定が違うだけ。chamomile-appで扱う用）
export interface GetContentListRequest {
  siteId: number
  contentId?: number,
  page?: number
  perPage?: number
  categoryId?: number | null
  status?: PublishStatus,
  title?: string
  orderBy?: ContentOrderBy[]
}

// GET コンテンツ一覧を取得するリクエストのうちGETパラメータに載せる情報
// (/sites/1234/contents?page=1&perPage=50 の？以降）
interface GetContentListRequestParams {
  page?: number | ''
  perPage?: number | ''
  categoryId?: string
  status?: PublishStatus | ''
  title?: string
  orderBy?: string
}

// PATCH コンテンツ情報を更新するリクエスト際の時に必要な情報
export interface UpdateContentItemRequest {
  siteId?: number
  page?: number
  perPage?: number
  contentId: number[]
  image?: string
  description?: string
  status?: PublishStatus | undefined
  publishedDate?: string
  expiresDate?: string
  category?: Category | undefined
  ogp?: OpenGraphProtocol
}

// コンテンツアイテムを表すデータのモデル定義
// 例えばGET contents を行うとこのモデルのリストが返ってくる(例：コンテンツ一覧)
export interface ContentItem {
  contentId?: number
  title?: string
  image?: string
  description?: string
  articleJson?: GerberaArticle,
  status?: PublishStatus
  createdDate?: string
  modifiedDate?: string
  publishedDate?: string
  expiresDate?: string
  siteId?: number
  category?: Category
  fileSize?: number
  ogp?: OpenGraphProtocol
}

export interface Category {
  categoryId?: number | null
  categoryName?: string
  siteId?: number
  createdDate?: string
  modifiedDate?: string
}

export interface OpenGraphProtocol {
  title?: string
  image?: string
  description?: string
}

export function isGerberaContainer(component: GerberaComponent): component is GerberaContainer {
  return (<GerberaContainer>component).children !== undefined
}

export interface GerberaComponent {
  readonly type: string,

  [key: string]: any
}

export interface GerberaContainer extends GerberaComponent {
  children: GerberaComponent[]
}

export interface GerberaHeader {
  background?: {
    color?: string
    image?: string
  }
}

export interface GerberaArticle extends GerberaContainer {
  header?: GerberaHeader
}

export interface DeleteContentItemRequest {
  siteId: number
  contentId: number[]
}

// 記事又は記事一覧を取得する
export interface GetContentListResponse extends BaseListResponse<ContentItem> {
}

export type UpdateContentItemResponse = ContentItem[]

export interface PingResponse {
  response: string
}

/**
 * コンテンツサービスのエンドポイント
 */
export interface IContentEndpoint {
  /** 記事1件を取得する */
  getContent(request: GetContentRequest): Promise<ContentItem>

  // 記事一覧を取得する
  getContentList(request: GetContentListRequest): Promise<GetContentListResponse>

  // コンテンツ情報を更新する
  updateContentItem(request: UpdateContentItemRequest): Promise<UpdateContentItemResponse>

  // コンテンツ情報をコピーする
  copyContentItem(request: GetContentRequest): Promise<ContentItem>

  // コンテンツを削除する
  deleteContentItem(request: DeleteContentItemRequest): Promise<number>

  // コンテンツの容量・容量上限を取得する
  loadStorageData(siteId: number): Promise<StorageData>

  // セッションを維持するための専用API
  pingRequest(): Promise<PingResponse>
}

export default class ContentEndpoint implements IContentEndpoint {
  /** 記事1件を取得する */
  getContent(request: GetContentRequest): Promise<ContentItem> {
    const path = `/sites/${request.siteId}/contents/${request.contentId}`
    return GerberaRequestFactory.getRequest().execute<ContentItem>(path)
      .then(response => response.data)
  }

  saveContent(request: ContentDataSet): Promise<ContentItem> {
    if (request.content.contentId) {
      return this.updateContent(request)
    }
    return this.addContent(request)
  }

  /** 記事一覧を取得する */
  getContentList(request: GetContentListRequest): Promise<GetContentListResponse> {
    // AとBを結合させる Object.assign(A, B)
    const baseRequest = Object.assign(<BaseApiRequestParams>{}, request)
    const baseParams = mapToRequestParams(baseRequest)
    const params = Object.assign(<GetContentListRequestParams>{}, baseParams)
    const path = `/sites/${request.siteId}/contents`
    return GerberaRequestFactory.getRequest().execute<GetContentListRequestParams, GetContentListResponse>(path, params)
      .then(response => response.data)
  }

  /** コンテンツ情報を一括更新する */
  updateContentItem(request: UpdateContentItemRequest): Promise<UpdateContentItemResponse> {
    if (request.contentId.length === 0) {
      throw new Error(`コンテンツIDは1つ以上指定して下さい`)
    }
    const contentId = request.contentId.join(',')
    const path = `/sites/${request.siteId}/contents/${contentId}`

    // APIの仕様に合わせて、requestにsiteIdとcontentIdを含めないようにする
    delete request.siteId
    delete request.contentId

    return GerberaRequestFactory.patchRequest().execute<UpdateContentItemRequest, UpdateContentItemResponse>(path, request)
      .then(response => response.data)
  }

  /** コンテンツをコピーする */
  copyContentItem(request: GetContentListRequest): Promise<ContentItem> {
    const contentId = request.contentId
    const path = `/sites/${request.siteId}/contents/${contentId}/copy`
    return GerberaRequestFactory.postRequest().execute<GetContentListRequest, ContentItem>(path, request)
      .then(response => response.data)
  }

  /** コンテンツを削除する */
  deleteContentItem(request: DeleteContentItemRequest): Promise<number> {
    if (request.contentId.length === 0) {
      throw new Error(`コンテンツIDは1つ以上指定して下さい`)
    }
    const contentId = request.contentId.join(',')
    const path = `/sites/${request.siteId}/contents/${contentId}`
    return GerberaRequestFactory.deleteRequest().execute<void>(path)
      .then(response => {
        // APIでは何も返さないが、削除対象の件数を返す。
        // 削除件数を表示するケースを考えてのこと。
        return request.contentId.length
      })
  }

  /** コンテンツの容量・容量上限を取得する */
  loadStorageData(siteId: number): Promise<StorageData> {
    const path = `/sites/${siteId}/stats`
    return GerberaRequestFactory.getRequest().execute<StorageData>(path)
      .then(response => response.data)
  }

  /** セッションを維持するための専用API */
  pingRequest(): Promise<PingResponse> {
    const path = `/ping`
    return GerberaRequestFactory.getRequest().execute<PingResponse>(path)
      .then(response => response.data)
  }

  private addContent(request: ContentDataSet): Promise<ContentItem> {
    const path = `/sites/${request.content.siteId}/contents`
    const formData = this.contentToFormData(request)
    return GerberaRequestFactory.postRequest().contentTypeAuto().execute<FormData, ContentItem>(path, formData)
      .then(response => response.data)
  }

  private updateContent(request: ContentDataSet): Promise<ContentItem> {
    const path = `/sites/${request.content.siteId}/contents/${request.content.contentId}`
    const formData = this.contentToFormData(request)
    return GerberaRequestFactory.putRequest().contentTypeAuto().execute<FormData, ContentItem>(path, formData)
      .then(response => response.data)
  }

  private contentToFormData(dataSet: ContentDataSet): FormData {
    const formData = new FormData()
    formData.append('es_content_info', JSON.stringify(dataSet.content))
    formData.append('es_content_ewml', dataSet.ewml)
    formData.append('es_content_attachment', new Blob([dataSet.css], { type: 'text/css' }), 'style.css')
    dataSet.images.forEach(image => {
      formData.append('es_content_attachment', new DataUri(image.dataUrl).toBlob(), image.fileName)
    })
    return formData
  }
}
